import type { FC } from 'react'
import Modal, { ModalProps } from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Draggable from './Draggable'

interface FFModalProps extends ModalProps {
  header?: React.ReactNode
  footer?: React.ReactNode
  fixedHeight?: boolean | undefined
}

// Draggable finds the header by this class.
const DRAG_HANDLE_CLASS = 'ff-modal-drag-handle'

/**
 * This modal component provides an abstraction on top of Material UI's
 * Modal component with some default styles
 * Fixed to center of the page
 * Uses <Paper /> component for container
 * Responsive
 * Handles child content overflow
 * Allows for fixed header and footer content
 * Draggable around the screen by the header (if a header is provided; making an element draggable prevents copying
 *   text; we assume this is okay for header content)
 */
export const FFModal: FC<FFModalProps> = props => {
  const { fixedHeight, header, footer, ...rest } = props
  return (
    <Modal {...rest}>
      {/* v5's Modal wraps its child in a FocusTrap that holds a ref to the
          child's DOM node; react-draggable's <Draggable> is a class component
          that doesn't forward one, so it must sit inside a real element that
          does. This wrapper is that element. */}
      <div style={{ outline: 'none' }}>
        <Draggable handle={`.${DRAG_HANDLE_CLASS}`}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Paper
              sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '60%',
                maxHeight: '90%',
                height: fixedHeight ? '90%' : undefined,
                overflow: 'auto',
                boxShadow: theme => `0 1rem 3rem 0 ${theme.palette.grey[900]}`,
              }}
            >
              {header && (
                <Box
                  className={DRAG_HANDLE_CLASS}
                  sx={{
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    borderBottom: '0.1rem solid',
                    borderBottomColor: 'grey.200',
                  }}
                >
                  {header}
                </Box>
              )}
              <Box sx={{ flex: 1, overflow: 'auto' }}>{props.children}</Box>
              {footer && (
                <Box sx={{ borderTop: '0.1rem solid', borderTopColor: 'grey.200' }}>{footer}</Box>
              )}
            </Paper>
          </Box>
        </Draggable>
      </div>
    </Modal>
  )
}
